import {
  array,
  boolean,
  enum_,
  type InferOutput,
  minLength,
  nullable,
  object,
  optional,
  pipe,
  string,
  number,
  integer,
  minValue,
  maxLength,
  maxValue,
  forward,
  check,
  transform
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { workMinutesSchema } from '@/features/shipment-orders/models'
import {
  BANK_ACCOUNT_TYPE,
  RECORDING_DATE_TYPE,
  ROUND_TYPE,
  TAX_CALCULATION_TYPE,
  SIGHT_MONTH_OFFSET,
  DUE_DATE_TYPE
} from '@/libs/api/types'

const BaseCreateCustomerDtoSchema = object({
  // 会社情報
  companyName: nullable(string()), // 会社名
  invoiceRegistrationNumber: nullable(string()), // 請求書登録番号

  // 基本情報
  name: pipe(string(), minLength(1, VALIDATION_MESSAGES.REQUIRED_ITEM)), // 荷主名
  code: pipe(string(), minLength(1, VALIDATION_MESSAGES.REQUIRED_ITEM)), // 荷主コード
  nameKana: nullable(string()), // 荷主名カナ
  phoneNumber: nullable(string()), // 電話番号
  faxNumber: nullable(string()), // FAX番号
  email: nullable(string()), // メールアドレス
  departmentName: nullable(string()), // 部署名
  enable: boolean(VALIDATION_MESSAGES.REQUIRED_ITEM), // 有効/無効
  taxCalculationType: enum_(TAX_CALCULATION_TYPE), // 税計算タイプ
  roundType: enum_(ROUND_TYPE), // 切り上げ・切り捨て
  isSubcontractFeeBillable: boolean(), // 下請手数料の請求可否

  // 住所情報
  postalCode: nullable(string()), // 郵便番号
  prefecture: nullable(string()), // 都道府県
  city: nullable(string()), // 市区町村
  blockNumber: nullable(string()), // 番地
  buildingName: nullable(string()), // 建物名

  // 振込先情報
  bankName: nullable(string()), // 銀行名
  bankCode: nullable(string()), // 銀行コード
  bankBranchCode: nullable(string()), // 銀行支店コード
  bankBranchName: nullable(string()), // 銀行支店名
  bankAccountType: enum_(BANK_ACCOUNT_TYPE), // 銀行口座種別
  bankAccountNumber: nullable(string()), // 銀行口座番号
  bankAccountName: nullable(string()), // 銀行口座名義
  sightMonthOffset: enum_(SIGHT_MONTH_OFFSET), // 支払いサイト
  dueDateType: enum_(DUE_DATE_TYPE), // 期日タイプ
  dueDay: nullable(
    pipe(
      number(),
      integer(VALIDATION_MESSAGES.INTEGER),
      minValue(1, VALIDATION_MESSAGES.MIN_VALUE(1)),
      maxValue(30, VALIDATION_MESSAGES.MAX_VALUE(30))
    )
  ), // 期日
  moneyType: string(), // 金種
  siteGroupCode: optional(
    nullable(
      pipe(
        string(),
        maxLength(50, VALIDATION_MESSAGES.MAX_LENGTH(50)),
        transform((v) => (v === '' ? null : v))
      )
    )
  ), // 関連地点紐付けコード（空文字は null として送信）
  defaultTaxCategoryId: nullable(number()), // デフォルト税区分ID（VOC_PJ-462）
  defaultIsTaxInclusive: optional(nullable(boolean())), // 内税既定（VOC_PJ-844 #3624 請求側 基本運賃カスケード: 取引先。null=未設定/継承）
  // 荷役作業の既定値（#4741）。null=未設定（配送依頼の作成時に初期値を出さない）
  defaultIsLoadingWorkOutsourced: optional(nullable(boolean())),
  defaultLoadingWorkMinutes: optional(workMinutesSchema),
  defaultIsUnloadingWorkOutsourced: optional(nullable(boolean())),
  defaultUnloadingWorkMinutes: optional(workMinutesSchema),
  defaultRoundType: optional(nullable(enum_(ROUND_TYPE))), // デフォルト端数処理（#4274 税メタカスケード: 取引先層。null=拠点設定に従う）
  billingShipperId: nullable(number()), // 請求先荷主ID（VOC_PJ-566,900 #4037 自己参照。null=自分自身が請求先）
  // VOC_PJ-55 #3516: 請求計上基準（null=拠点設定に従う）
  incomeRecordingDateType: nullable(enum_(RECORDING_DATE_TYPE)),
  /**
   * VOC_PJ-29,431 #1857: デフォルト付帯費用カテゴリ ID 配列。
   * 別 endpoint で保存するため、現状は form schema 内に値を保持するだけで PUT には載せない。
   * (Customer Form の保存ハンドラ側で別 endpoint を呼び分ける)
   */
  defaultAdditionalPaymentCategoryIds: optional(array(number()), [])
})

export const CreateCustomerDtoSchema = pipe(
  BaseCreateCustomerDtoSchema,
  forward(
    check(
      (v) => v.dueDateType !== DUE_DATE_TYPE.DAY_OF_MONTH || v.dueDay != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['dueDay']
  )
)

export type CreateCustomerDto = InferOutput<typeof CreateCustomerDtoSchema>
